import { groupBy, keyBy } from '@/packages/array';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { toLocalTimezone } from './timezones';

dayjs.extend(isSameOrBefore);

type EpisodeWorkingData = {
    showSchedules: DaySchedules;
    startTimeDayjs: Dayjs;
    startTimeIso: string;
    day: number;
};

// Provided for readability:
type WorkingEpisodeRange = EpisodeWorkingData[];
type GroupedWorkingEpisodeRange = WorkingEpisodeRange[];
type GroupedWorkingEpisodeRanges = GroupedWorkingEpisodeRange[];

export function getUnbrokenEpisodeDateRangeGroups(show: SiteChannelShow) {
    const workingFlattenedLocalTimeEpisodes =
        show.show_schedules?.flatMap((schedule) => addWorkingData(schedule)) ?? [];

    const uniqueEpisodes = Object.values(keyBy('startTimeIso', workingFlattenedLocalTimeEpisodes));

    const dayGroupedSortedEpisodes = Object.values(groupBy('day', uniqueEpisodes)).sort(
        (groupA, groupB) => getWeekdayIndex(groupA[0].startTimeDayjs) - getWeekdayIndex(groupB[0].startTimeDayjs),
    );

    const groupedUnbrokenEpisodeDateRanges = dayGroupedSortedEpisodes.reduce<GroupedWorkingEpisodeRanges>(
        (unbrokenDateRangesSoFar, currentDayGroup) => {
            if (!unbrokenDateRangesSoFar?.length) {
                return [[currentDayGroup]];
            }

            const completedUnbrokenRanges = [...unbrokenDateRangesSoFar];
            const inProgressUnbrokenRange = completedUnbrokenRanges.pop() ?? [];
            const inProgressRangeLatestDay = [...inProgressUnbrokenRange].pop() ?? [];

            const currentContinuesRange = doesCurrentContinueRange(inProgressRangeLatestDay, currentDayGroup);

            const currentAddedToInProgressIfContinuesRange = [
                ...inProgressUnbrokenRange,
                ...(currentContinuesRange ? [currentDayGroup] : []),
            ];
            const newRangeIfCurrentBreaksRange = currentContinuesRange ? [] : [[currentDayGroup]];

            return [
                ...completedUnbrokenRanges,
                currentAddedToInProgressIfContinuesRange,
                ...newRangeIfCurrentBreaksRange,
            ];
        },
        [],
    );

    return removeWorkingData(groupedUnbrokenEpisodeDateRanges);
}

function addWorkingData(schedule: ShowSchedules) {
    return schedule.day_schedules.map<EpisodeWorkingData>((episode) => {
        const startTimeDayjs = dayjs(toLocalTimezone(episode.start_time));

        return {
            showSchedules: episode,
            startTimeDayjs,
            startTimeIso: startTimeDayjs.toISOString(),
            day: startTimeDayjs.date(),
        };
    });
}

function removeWorkingData(unbrokenDateRanges: GroupedWorkingEpisodeRanges) {
    return unbrokenDateRanges.map((unbrokenDateRange) =>
        unbrokenDateRange.map((dayGroup) => dayGroup.map(({ showSchedules: episode }) => episode)),
    );
}

function doesCurrentContinueRange(
    inProgressRangeLatestDayRange: WorkingEpisodeRange,
    currentDayRange: WorkingEpisodeRange,
) {
    return !!(
        inProgressRangeLatestDayRange.length &&
        currentDayRange.length === inProgressRangeLatestDayRange.length &&
        currentDayRange[0].startTimeDayjs.isSameOrBefore(
            inProgressRangeLatestDayRange[0].startTimeDayjs.add(1, 'day'),
        ) &&
        currentDayRange.reduce((rangeIsUnbrokenSoFar, { startTimeDayjs: currentDayStartTime }, currentIndex) => {
            const inProgressGroupDaySampleEpisode = inProgressRangeLatestDayRange[currentIndex];

            return (
                rangeIsUnbrokenSoFar &&
                currentDayStartTime
                    .year(inProgressGroupDaySampleEpisode.startTimeDayjs.year())
                    .month(inProgressGroupDaySampleEpisode.startTimeDayjs.month())
                    .date(inProgressGroupDaySampleEpisode.startTimeDayjs.date())
                    .isSame(inProgressGroupDaySampleEpisode.startTimeDayjs, 'hour')
            );
        }, true)
    );
}

function getWeekdayIndex(date: Dayjs): number {
    const dayOfWeek = date.day();
    // Sunday is `0` by default. We reassign it to `7` for the purpose of our desired sorting (Monday first):
    return dayOfWeek === 0 ? 7 : dayOfWeek;
}
